import React, { useEffect, useRef, useState } from "react";
import {
  closeSearch,
  getSearchState,
  openSearch,
  setSearchState,
  subscribeSearch,
} from "./SearchManager";
import { pushNotification } from "./NotificationManager";
import { searchNapflix } from "./SearchProvider";
import {
  handleBack,
  registerBackHandler,
  unregisterBackHandler,
} from "./BackManager";

// NapflixOS Universal Search v9/v14
// OS-level search shell with read-only app/API result providers.
export default function SearchOverlay() {
  const [state, setState] = useState(getSearchState());
  const inputRef = useRef(null);

  useEffect(() => {
    setState(getSearchState());
    return subscribeSearch(setState);
  }, []);

  useEffect(() => {
    registerBackHandler({
      id: "search-overlay",
      priority: 100,
      canHandle: () => getSearchState().open,
      handle: () => {
        closeSearch();
        return true;
      },
      label: "Search Overlay",
    });

    return () => unregisterBackHandler("search-overlay");
  }, []);

  useEffect(() => {
    if (!state.open) return undefined;

    const timer = window.setTimeout(() => {
      inputRef.current?.focus?.({ preventScroll: true });
    }, 0);

    return () => window.clearTimeout(timer);
  }, [state.open]);

  useEffect(() => {
    if (!state.open) return undefined;

    const query = state.query.trim();
    if (query.length < 2) {
      setSearchState({ results: [], loading: false, error: null, selectedIndex: 0 });
      return undefined;
    }

    let cancelled = false;
    setSearchState({ loading: true, error: null });

    const timer = window.setTimeout(() => {
      searchNapflix(query)
        .then((results) => {
          if (cancelled) return;
          setSearchState({
            results,
            loading: false,
            error: null,
            selectedIndex: 0,
          });
        })
        .catch((error) => {
          if (cancelled) return;
          setSearchState({
            results: [],
            loading: false,
            error: error?.message || "Search failed",
            selectedIndex: 0,
          });
        });
    }, 200);

    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [state.open, state.query]);

  useEffect(() => {
    const onKey = (event) => {
      const shortcut =
        (event.metaKey || event.ctrlKey) && event.key.toLowerCase() === "f";

      if (shortcut) {
        event.preventDefault();
        event.stopImmediatePropagation?.() || event.stopPropagation();
        openSearch();
        return;
      }

      const currentState = getSearchState();
      if (!currentState.open) return;

      if (event.key === "ArrowDown") {
        event.preventDefault();
        event.stopImmediatePropagation?.() || event.stopPropagation();
        setSearchState({
          selectedIndex: Math.min(
            currentState.results.length - 1,
            currentState.selectedIndex + 1
          ),
        });
        return;
      }

      if (event.key === "ArrowUp") {
        event.preventDefault();
        event.stopImmediatePropagation?.() || event.stopPropagation();
        setSearchState({
          selectedIndex: Math.max(0, currentState.selectedIndex - 1),
        });
        return;
      }

      if (event.key === "Escape") {
        event.preventDefault();
        event.stopImmediatePropagation?.() || event.stopPropagation();
        handleBack("keyboard");
        return;
      }

      if (event.key === "Backspace" && currentState.query.length === 0) {
        event.preventDefault();
        event.stopImmediatePropagation?.() || event.stopPropagation();
        handleBack("keyboard");
        return;
      }

      if (event.key === "Enter") {
        event.preventDefault();
        event.stopImmediatePropagation?.() || event.stopPropagation();

        const selected = currentState.results[currentState.selectedIndex];
        if (selected?.type === "pjtv-channel") {
          const channelNumber = selected.payload?.channelNumber;

          window.dispatchEvent(new CustomEvent("pj:setPage", { detail: "tv" }));
          window.setTimeout(() => {
            window.dispatchEvent(
              new CustomEvent("pjtv:focusChannel", {
                detail: { channelNumber },
              })
            );
          }, 80);
          closeSearch();
          return;
        }

        if (selected?.type === "movie" || selected?.type === "show") {
          window.dispatchEvent(
            new CustomEvent("pj:openItem", {
              detail: {
                type: selected.type,
                ratingKey: selected.payload?.ratingKey,
                item: selected.payload,
              },
            })
          );
          closeSearch();
          return;
        }

        if (selected?.type?.startsWith("music-")) {
          window.dispatchEvent(new CustomEvent("pj:setPage", { detail: "music" }));
          window.setTimeout(() => {
            window.dispatchEvent(
              new CustomEvent("pj:musicOpen", {
                detail: {
                  type: selected.type.replace("music-", ""),
                  ratingKey: selected.payload?.ratingKey,
                  item: selected.payload?.item,
                },
              })
            );
          }, 80);
          closeSearch();
          return;
        }

        pushNotification({
          type: "search",
          title: "Universal Search",
          message: "Start typing to search movies, shows, music, and PJTV.",
          level: "info",
        });
      }
    };

    window.addEventListener("keydown", onKey, true);
    return () => window.removeEventListener("keydown", onKey, true);
  }, []);

  if (!state.open) return null;

  return (
    <div
      aria-label="Napflix search"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) event.preventDefault();
      }}
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 2147483250,
        display: "flex",
        alignItems: "flex-start",
        justifyContent: "center",
        paddingTop: "14vh",
        pointerEvents: "auto",
        background:
          "radial-gradient(circle at 50% 20%, rgba(32, 44, 72, 0.48), rgba(0,0,0,0.68) 68%)",
      }}
    >
      <style>{`
        @keyframes napflixSearchIn {
          from { opacity: 0; transform: translate3d(0, -10px, 0) scale(0.98); }
          to { opacity: 1; transform: translate3d(0, 0, 0) scale(1); }
        }
      `}</style>

      <div
        style={{
          width: "min(680px, calc(100vw - 56px))",
          borderRadius: 20,
          border: "1px solid rgba(255,255,255,0.18)",
          background: "rgba(8, 12, 22, 0.9)",
          boxShadow:
            "0 30px 90px rgba(0,0,0,0.66), inset 0 1px 0 rgba(255,255,255,0.12)",
          backdropFilter: "blur(20px) saturate(1.25)",
          color: "rgba(255,255,255,0.92)",
          overflow: "hidden",
          animation: "napflixSearchIn 180ms ease both",
        }}
      >
        <div
          style={{
            padding: "18px 22px 10px",
            fontFamily: "'Outfit', sans-serif",
            fontSize: 11,
            letterSpacing: 2.4,
            textTransform: "uppercase",
            color: "rgba(229, 199, 93, 0.9)",
          }}
        >
          Universal Search
        </div>

        <div style={{ padding: "0 22px 24px" }}>
          <input
            ref={inputRef}
            value={state.query}
            placeholder="Search Napflix..."
            onChange={(event) => setSearchState({ query: event.target.value })}
            style={{
              width: "100%",
              boxSizing: "border-box",
              border: 0,
              outline: 0,
              borderRadius: 14,
              padding: "18px 18px",
              background: "rgba(255,255,255,0.08)",
              color: "rgba(255,255,255,0.96)",
              fontFamily: "'Outfit', sans-serif",
              fontSize: 26,
              boxShadow:
                "inset 0 0 0 1px rgba(255,255,255,0.12), 0 0 0 1px rgba(229,160,13,0.12)",
            }}
          />

          <SearchResults state={state} />
        </div>
      </div>
    </div>
  );
}

function SearchResults({ state }) {
  const selectedResultRef = useRef(null);
  const queryReady = state.query.trim().length >= 2;

  useEffect(() => {
    selectedResultRef.current?.scrollIntoView?.({ block: "nearest" });
  }, [state.selectedIndex]);

  if (!queryReady) {
    return (
      <SearchMessage>
        Start typing to search movies, shows, music, and PJTV.
      </SearchMessage>
    );
  }

  if (state.loading) {
    return <SearchMessage>Searching Napflix...</SearchMessage>;
  }

  if (state.error) {
    return <SearchMessage>Search is unavailable right now.</SearchMessage>;
  }

  if (!state.results.length) {
    return <SearchMessage>No results found.</SearchMessage>;
  }

  let lastSource = "";

  return (
    <div
      style={{
        marginTop: 16,
        maxHeight: "min(48vh, 420px)",
        overflowY: "auto",
        fontFamily: "'Outfit', sans-serif",
      }}
    >
      {state.results.map((result, index) => {
        const selected = index === state.selectedIndex;
        const showSource = result.source !== lastSource;
        lastSource = result.source;

        return (
          <React.Fragment key={result.id}>
            {showSource && (
              <div
                style={{
                  padding: "12px 4px 6px",
                  fontSize: 10,
                  letterSpacing: 1.8,
                  textTransform: "uppercase",
                  color: "rgba(229, 199, 93, 0.72)",
                }}
              >
                {result.source}
              </div>
            )}
            <div
              ref={selected ? selectedResultRef : null}
              data-search-result-index={index}
              style={{
                display: "grid",
                gridTemplateColumns: "52px 1fr",
                gap: 12,
                alignItems: "center",
                minHeight: 62,
                padding: "8px 10px",
                borderRadius: 12,
                background: selected
                  ? "rgba(229, 160, 13, 0.18)"
                  : "rgba(255,255,255,0.045)",
                boxShadow: selected
                  ? "inset 0 0 0 1px rgba(229, 199, 93, 0.62)"
                  : "inset 0 0 0 1px rgba(255,255,255,0.07)",
                color: "rgba(255,255,255,0.92)",
              }}
            >
              <div
                style={{
                  width: 52,
                  height: 52,
                  borderRadius: 10,
                  overflow: "hidden",
                  background: "rgba(255,255,255,0.08)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  color: "rgba(255,255,255,0.42)",
                  fontSize: 10,
                }}
              >
                {result.artwork ? (
                  <img
                    src={result.artwork}
                    alt=""
                    style={{ width: "100%", height: "100%", objectFit: "cover" }}
                  />
                ) : (
                  result.type.toUpperCase().slice(0, 4)
                )}
              </div>
              <div style={{ minWidth: 0 }}>
                <div
                  style={{
                    fontSize: 16,
                    fontWeight: 600,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  {result.title}
                </div>
                <div
                  style={{
                    marginTop: 3,
                    fontSize: 12,
                    color: "rgba(255,255,255,0.56)",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  {result.subtitle || result.type}
                </div>
              </div>
            </div>
          </React.Fragment>
        );
      })}
    </div>
  );
}

function SearchMessage({ children }) {
  return (
    <div
      style={{
        marginTop: 18,
        padding: "18px 4px 2px",
        fontFamily: "'Outfit', sans-serif",
        fontSize: 14,
        lineHeight: 1.5,
        color: "rgba(255,255,255,0.56)",
      }}
    >
      {children}
    </div>
  );
}
